import type { TranslateFn } from "../../i18n";
import { useIsMobile } from "../utils/useIsMobile";

/**
 * Empty state shown when the backend can't find a size chart for the
 * product (no merchant CSV uploaded, no inline size guide). The user
 * still gets a polite path forward — skip sizing entirely and try the
 * product on visually.
 *
 * Reuses the same .ps-tryon-sr-split / .ps-tryon-sr-img-col /
 * .ps-tryon-sr-right-col structure as SizeResultView, so the modal stays
 * at its normal width/height — only the right-column content swaps to
 * the no-chart message + CTAs.
 */
export function NoChartView({
	productImage,
	productTitle,
	reason = "no-chart",
	onTryOn,
	onClose,
	t,
}: {
	productImage?: string;
	productTitle?: string;
	reason?: "no-chart" | "no-match";
	onTryOn: () => void;
	onClose: () => void;
	t: TranslateFn;
}) {
	const isMobile = useIsMobile();
	const isNoMatch = reason === "no-match";

	const RightColumn = (
		<div className="ps-tryon-no-chart-content">
			<div className="ps-tryon-no-chart-icon" aria-hidden="true">
				<svg viewBox="0 0 24 24" width="44" height="44" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
					<path d="M3 17l6 6 12-12-6-6L3 17z" />
					<path d="M14 4l-3 3M16 6l-2 2M18 8l-2 2M11 7l-2 2M13 9l-2 2M15 11l-2 2" />
				</svg>
			</div>
			<h3 className="ps-tryon-no-chart-title">
				{isNoMatch ? t("No matching size available") : t("No size chart available")}
			</h3>
			<p className="ps-tryon-no-chart-msg">
				{isNoMatch
					? t("This product's size chart doesn't include a reliable fit for your measurements.")
					: t("The merchant hasn't uploaded sizing data for this product yet. You can still see how it looks on you with a virtual try-on.")}
			</p>
			<div className="ps-tryon-no-chart-actions">
				{!isNoMatch && (
					<button type="button" className="ps-tryon-no-chart-cta" onClick={onTryOn}>
						{t("See how it looks on you")} &rarr;
					</button>
				)}
				<button type="button" className="ps-tryon-no-chart-secondary" onClick={onClose}>
					{t("Continue Shopping")}
				</button>
			</div>
		</div>
	);

	if (isMobile) {
		// Mobile keeps the stacked layout — same as SizeResultView's mobile
		// flow renders product photo on top with content below. Reuse the
		// existing mobile-specific styles.
		return (
			<div className="ps-tryon-sr">
				{productImage && (
					<div className="ps-tryon-sr-img-col" style={{ flex: "0 0 auto", maxHeight: "38vh" }}>
						<img src={productImage} alt={productTitle || ""} className="ps-tryon-sr-product-img" />
					</div>
				)}
				<div className="ps-tryon-no-chart-right-col">{RightColumn}</div>
			</div>
		);
	}

	return (
		<div className="ps-tryon-sr">
			<div className="ps-tryon-sr-split">
				<div className="ps-tryon-sr-img-col">
					{productImage && (
						<img src={productImage} alt={productTitle || ""} className="ps-tryon-sr-product-img" />
					)}
				</div>
				<div className="ps-tryon-sr-right-col ps-tryon-no-chart-right-col">
					{RightColumn}
				</div>
			</div>
		</div>
	);
}
